import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import Highcharts from '../../ChartSettings/HighchartsConfig';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import api from "../../../services/api";
import { highchartsLabelFormatterFromMs, highchartsCrosshairFormatterFromMs } from "../../../utils/dateFormatter";
import mergeDeep from '../../../utils/mergeDeep';
import * as ChartConsts from '../../ChartSettings/ChartSettings';
import MarkerEditingManager from './MarkerEditingManager';

// R-peak marker symbol - rectangle extending upward from point
Highcharts.SVGRenderer.prototype.symbols.rectangleUp = function (x, y, w, h) {
  const rectWidth = w / 4;  // Half width (twice thinner)
  const rectHeight = h * 1.5;  // Half height (twice shorter)
  const cx = x + w / 2;  // center x of bounding box (properly centered on data point)

  return ['M', cx - rectWidth / 2, y + h,
    'L', cx - rectWidth / 2, y + h - rectHeight,
    'L', cx + rectWidth / 2, y + h - rectHeight,
    'L', cx + rectWidth / 2, y + h,
    'z'];
};

// Non-R marker symbol - rectangle extending downward from point
Highcharts.SVGRenderer.prototype.symbols.rectangleDown = function (x, y, w, h) {
  const rectWidth = w / 4;  // Half width (twice thinner)
  const rectHeight = h * 1.5;  // Half height (twice shorter)
  const cx = x + w / 2;  // center x of bounding box (properly centered on data point)

  return ['M', cx - rectWidth / 2, y,
    'L', cx - rectWidth / 2, y + rectHeight,
    'L', cx + rectWidth / 2, y + rectHeight,
    'L', cx + rectWidth / 2, y,
    'z'];
};

// Comment marker symbol - speech bubble shape
Highcharts.SVGRenderer.prototype.symbols.commentBubble = function (x, y, w, h) {
  const size = 20;
  const cx = x + w / 2;
  const cy = y + h / 2;
  
  // Create speech bubble path
  return [
    'M', cx - size/2, cy - size/2,
    'L', cx + size/2, cy - size/2,
    'L', cx + size/2, cy + size/4,
    'L', cx + size/4, cy + size/2,
    'L', cx, cy + size/4,
    'L', cx - size/2, cy + size/4,
    'Z'
  ];
};

const EcgPreviewChartForwardRef = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const chartPreviewComponentRef = useRef(null);
  const loadedDataRange = useRef({ min: null, max: null });
  const cachedChartData = useRef(null);
  const asyncDataLoadUrlTemplate = useRef(null);
  const maxXAxisWidth = useRef(10000);
  const currentChartSpeed = useRef(null);
  const maxRecordLength = useRef(0);
  const markersEnabled = useRef(false);
  const cachedMarkers = useRef(null);
  const cachedCommentMarkers = useRef(null);
  const viewportUpdateTimeout = useRef(null);
  const isLastRow = useRef(false); // Track if we're viewing the last navigation row
  const latestReceivedTime = useRef(0); // Track the latest time from online data
  const forceAutoScroll = useRef(false); // Force auto-scroll on next data arrival (one-time override)
  const extendedMaxTime = useRef(null); // Extended max time for showing empty space on last row
  const shouldScrollToLastPoint = useRef(false); // Flag to scroll to last point after data loads
  const pendingApiController = useRef(null); // AbortController to cancel pending API requests
  const pendingRequestRange = useRef(null); // Track in-flight request intent {min, max, center, isLastRow}
  //const viewModeRef = useRef(2); // View mode: 0=continuous, 1=refresh, 2=live (default live mode)

  // Marker editing manager instance (handles add/delete logic)
  const markerEditingManager = useRef(new MarkerEditingManager()).current;
  const isEditModeRef = useRef(false);
  const onMarkerAddedRef = useRef(null);
  const onMarkerDeletedRef = useRef(null);
  const onRPeakClickRef = useRef(null);

  // Keep callback refs in sync with props (callbacks are stable, won't cause re-renders)
  onMarkerAddedRef.current = props.onMarkerAdded;
  onMarkerDeletedRef.current = props.onMarkerDeleted;
  onRPeakClickRef.current = props.onRPeakClick;

  const PREVIEW_SERIES_NAME = 'preview-series-data';
  const COMMENT_MARKERS_PREVIEW_NAME = 'comment-markers-preview';
  const TOTAL_CELLS_ON_Y = 30;
  const MAKE_SQUARE_CELLS_ON_ANY_MONITOR_COEFFICIENT = 0.9;

  // Function to update marker display (called imperatively, not via useEffect)
  const updateMarkerDisplay = () => {
    if (!markersEnabled.current) return;

    const chart = chartPreviewComponentRef.current?.chart;
    if (!chart || !chart.get) return;

    const effectiveMarkers = markerEditingManager.getEffectiveMarkers();
    const { additions, deletions } = markerEditingManager.getPendingChanges();
    console.log('Updating markers display:', {
      original: cachedMarkers.current?.length || 0,
      additions: additions.length,
      deletions: deletions.length,
      effective: effectiveMarkers.length
    });

    // Clear existing markers first, then apply effective markers
    if (cachedChartData.current) {
      const series = chart.get(PREVIEW_SERIES_NAME);
      if (series) {
        // Reset to base data
        series.setData(cachedChartData.current, false);
        // Apply effective markers
        if (effectiveMarkers.length > 0) {
          applyMarkersToChart(chart, effectiveMarkers);
        }
        chart.redraw();
      }
    }
  };

  // Internal function to apply markers to chart (extracted for reuse)
  const applyMarkersToChart = (chart, points) => {
    const series = chart.get(PREVIEW_SERIES_NAME);
    if (!series) return;

    if (points && points.length > 0) {
      const newData = series.options.data.map(dataPoint => {
        const marker = points.find(m => m.x === dataPoint[0]);

        if (marker) {
          const smb = marker.isR ? 'rectangleUp' : 'rectangleDown';
          // Use semi-transparent colors so markers appear behind the ECG line
          const clr = 'rgba(0, 128, 0, 0.8)';  // All markers green
          const label = {};
          if (marker.title) {
            label.format = marker.title;
            label.enabled = true;
            label.x = 0;
            // R peaks: label on top (negative y), other markers: label at bottom of tick (positive y)
            label.y = marker.isR ? -15 : 15;
            label.verticalAlign = marker.isR ? 'bottom' : 'top';
          }

          return {
            x: dataPoint[0],
            y: dataPoint[1],
            marker: {
              symbol: smb,
              enabled: true,
              lineColor: clr,
              fillColor: clr,
              states: { hover: { enabled: true } }
            },
            dataLabels: label
          };
        }
        return dataPoint;
      });

      series.setData(newData, false);
    }
  };

  // Auto-scroll configuration for online streaming
  // When new data reaches this percentage of viewport width, trigger auto-scroll
  const AUTO_SCROLL_TRIGGER_THRESHOLD = 0.95; // 90% (last 10% of viewport)
  // Position new data at this percentage from viewport start after auto-scroll
  const AUTO_SCROLL_TARGET_POSITION = 0.1; // 10% from start

  const getPreviewChart = () => {
    if (!chartPreviewComponentRef.current || !chartPreviewComponentRef.current.chart) {
      return null;
    }
    return chartPreviewComponentRef.current.chart;
  };

  // Helper function for tick positioning - same as OnlineHighchartsAsyncWithNav
  const getChartTickPositions = function (xAxis, cellTimeMs) {
    let positions = [],
      min = xAxis.min,
      max = xAxis.max,
      increment = cellTimeMs * 10,
      tick = Math.floor(min / increment) * increment;

    //console.log('getChartTickPositions called:', {
    //  min, max, cellTimeMs, increment, tick,
    //  axisDataMin: xAxis.dataMin,
    //  axisDataMax: xAxis.dataMax
    //});

    if (max !== null && min !== null) {
      // Safeguard: limit the number of ticks to prevent memory issues
      const maxTicks = 1000; // Maximum reasonable number of ticks
      let tickCount = 0;
      
      for (tick; tick - increment <= max && tickCount < maxTicks; tick += increment) {
        positions.push(tick);
        tickCount++;
      }
    }
    
    //console.log('[debug] getChartTickPositions returning positions:', positions.length, 'ticks');
    return positions;
  };

  // Helper method to check if data is already cached for the given range
  const isDataCached = (min, max) => {
    return loadedDataRange.current.min === min && 
           loadedDataRange.current.max === max && 
           cachedChartData.current;
  };

  // Helper method to create series configuration
  const createSeriesConfig = (data) => {
    const config = {
      id: PREVIEW_SERIES_NAME,
      name: props.seriesName,
      data: data,
      color: 'blue',
      lineWidth: 2,
      enableMouseTracking: true,
      marker: {
        states: {
          hover: {
            enabled: false
          }
        }
      },
      tooltip: {
        enabled: false
      }
    };
    
    // Only add showInNavigator if navigator is enabled
    const chartOptions = getPreviewOptions();
    if (chartOptions.navigator && chartOptions.navigator.enabled !== false) {
      config.showInNavigator = false;
    }
    
    return config;
  };

  // Helper method to notify parent that loading is complete
  const notifyLoadComplete = () => {
    if (props.onLoadComplete) {
      props.onLoadComplete();
    }
  };

  // Helper method to calculate viewport bounds
  const calculateViewportBounds = (centerTimeMs, data, cellTimeMs) => {
    // Use the actual calculated visible time based on chart dimensions and cell time
    const visibleTimeMs = calcMaxXAxisVisibleTimeMs(cellTimeMs || currentChartSpeed.current || props.defaultCellTimeXms);
    let previewStartMs = centerTimeMs - (visibleTimeMs / 2);
    let previewEndMs = centerTimeMs + (visibleTimeMs / 2);

    // Determine the effective data bounds
    const actualDataStart = data[0][0];
    const actualDataEnd = data[data.length - 1][0];

    // If we have an extended max (for last row with empty space), use it instead of actualDataEnd
    const effectiveMaxTime = extendedMaxTime.current || actualDataEnd;

    if (previewStartMs < actualDataStart) {
      previewStartMs = actualDataStart;
      previewEndMs = Math.min(actualDataStart + visibleTimeMs, effectiveMaxTime);
    }
    if (previewEndMs > effectiveMaxTime) {
      previewEndMs = effectiveMaxTime;
      previewStartMs = Math.max(effectiveMaxTime - visibleTimeMs, actualDataStart);
    }

    return { previewStartMs, previewEndMs };
  };

  // Helper method to ensure preview series exists and has correct data
  const ensurePreviewSeries = (chart, data, recreate = false) => {
    let previewSeries = chart.get(PREVIEW_SERIES_NAME);
    const seriesConfig = createSeriesConfig(data);
    
    if (!previewSeries) {
      // Series doesn't exist, create it
      console.log('Creating new preview series');
      chart.addSeries(seriesConfig, false);
    } else if (recreate || previewSeries.options.data.length === 0) {
      // Series exists but needs recreation or has no data
      console.log('Recreating preview series');
      previewSeries.remove(false);
      chart.addSeries(seriesConfig, false);
    } else if (previewSeries.options.data.length !== data.length || 
      (previewSeries.options.data.length > 1 && previewSeries.options.data[0][0] !== data[0][0])) {
      // Series exists but has different data length
      console.log('Updating series data due to length mismatch');
      //previewSeries.remove(false);
      //chart.addSeries(seriesConfig, false);

      previewSeries.setData(data, true);
    } else {
      // Series exists and has correct data
      console.log('Series exists with correct data', previewSeries.data[0], data[0]);            
    }
    
    return chart.get(PREVIEW_SERIES_NAME);
  };

  // Helper method to handle series restoration after setExtremes
  const restoreSeriesAfterExtremes = (chart, data, previewStartMs, previewEndMs) => {
    setTimeout(() => {
      const series = chart.get(PREVIEW_SERIES_NAME);
      console.log('Checking series after setExtremes:', {
        hasData: series && series.data.length > 0,
        dataLength: series ? series.data.length : 0
      });
      
      // If series was destroyed by setExtremes, recreate it
      if (!series || series.data.length === 0) {
        console.log('Series was destroyed, recreating with cached data');
        
        if (series) {
          series.remove(false);
        }
        
        chart.addSeries(createSeriesConfig(data), true);
        chart.xAxis[0].setExtremes(previewStartMs, previewEndMs, true);
        setChartExtremesX(chart, currentChartSpeed.current ?? props.defaultCellTimeXms);
        
        // Re-apply markers after series recreation if enabled
        if (markersEnabled.current && cachedMarkers.current) {
          console.log('Re-applying markers after series recreation');
          setTimeout(() => {
            markPointsOnChart(chart, cachedMarkers.current);
          }, 100);
        }
        
        // Re-apply comment markers after series recreation
        if (cachedCommentMarkers.current) {
          console.log('Re-applying comment markers after series recreation');
          setTimeout(() => {
            markCommentPointsOnChart(chart, cachedCommentMarkers.current);
          }, 200);
        }
      }
    }, 10);
  };

  // Helper method to update viewport for cached data
  const updateViewportForCachedData = (chart, centerTimeMs) => {
    if (!cachedChartData.current || cachedChartData.current.length === 0) {
      console.log('No cached data available for viewport update');
      return;
    }
    
    console.log('Updating preview chart viewport for cached data');
    
    // Ensure series exists with cached data
    ensurePreviewSeries(chart, cachedChartData.current);
    
    // Calculate and set viewport
    const { previewStartMs, previewEndMs } = calculateViewportBounds(centerTimeMs, cachedChartData.current);
    
    console.log('Setting preview chart extremes for cached data:', {
      previewStartMs,
      previewEndMs,
      centerTimeMs
    });
    
    // Update extremes on preview chart
    chart.xAxis[0].setExtremes(previewStartMs, previewEndMs, true);
    
    // Handle potential series destruction after setExtremes
    restoreSeriesAfterExtremes(chart, cachedChartData.current, previewStartMs, previewEndMs);
    
    // Re-apply markers after viewport change if enabled
    if (markersEnabled.current && cachedMarkers.current) {
      console.log('Re-applying markers after viewport update');
      setTimeout(() => {
        const chartForMarkers = getPreviewChart();
        if (chartForMarkers && chartForMarkers.get) {
          markPointsOnChart(chartForMarkers, cachedMarkers.current);
        }
      }, 100);
    }       
  };

  // Helper method to handle API response
  const handleApiResponse = (response, centerTimeMs) => {
    const currentChart = getPreviewChart();
    if (!currentChart || !currentChart.get) {
      console.warn('Chart no longer available after async load');
      // Always notify parent that loading is complete
      notifyLoadComplete();
      return;
    }
    
    const receivedData = response.data;
    console.log('Data received from API:', {
      totalPoints: receivedData.data ? receivedData.data.length : 0,
      firstPoint: receivedData.data && receivedData.data.length > 0 ? receivedData.data[0] : null,
      lastPoint: receivedData.data && receivedData.data.length > 0 ? receivedData.data[receivedData.data.length - 1] : null,
      markersCount: receivedData.markers ? receivedData.markers.length : 0,
      commentMarkersCount: receivedData.commentMarkers ? receivedData.commentMarkers.length : 0
    });
    
    // Cache the data and markers
    cachedChartData.current = receivedData.data;
    cachedMarkers.current = receivedData.markers;
    cachedCommentMarkers.current = receivedData.commentMarkers;

    // Initialize marker editing manager with original markers
    markerEditingManager.setOriginalMarkers(receivedData.markers);
    
    // Set data in the preview chart
    if (receivedData.data && receivedData.data.length > 0) {
      console.log('Setting data in preview chart');
      
      // Ensure series exists and set data
      ensurePreviewSeries(currentChart, receivedData.data);
      
      // Calculate and set viewport
      const { previewStartMs, previewEndMs } = calculateViewportBounds(centerTimeMs, receivedData.data);
      
      console.log('Setting preview chart extremes:', {
        previewStartMs,
        previewEndMs,
        centerTimeMs
      });
      
      currentChart.xAxis[0].setExtremes(previewStartMs, previewEndMs, true);
      
      // Display markers if enabled
      if (receivedData.markers && markersEnabled.current) {
        console.log('Displaying markers on preview chart');
        setTimeout(() => {
          const chartForMarkers = getPreviewChart();
          if (chartForMarkers && chartForMarkers.get) {
            markPointsOnChart(chartForMarkers, receivedData.markers);
          }
        }, 500);
      }
      
      // Display comment markers
      if (receivedData.commentMarkers) {
        console.log('Displaying comment markers on preview chart');
        setTimeout(() => {
          const chartForComments = getPreviewChart();
          if (chartForComments && chartForComments.get) {
            markCommentPointsOnChart(chartForComments, receivedData.commentMarkers);
          }
        }, 700); // Slightly after regular markers
      }
    }
    
    // Hide loading indicator
    try {
      currentChart.hideLoading();
    } catch (e) {
      console.warn('Error hiding loading on chart:', e);
    }

    // Check if we should scroll to last point after data load
    console.log('Fresh data path: Checking shouldScrollToLastPoint.current =', shouldScrollToLastPoint.current);
    if (shouldScrollToLastPoint.current) {
      console.log('Fresh data loaded - scrolling to last point as requested');
      // Note: Flag will be reset inside scrollToLastDataPointInternal after scroll completes
      // Small delay to ensure data is fully rendered
      setTimeout(() => {
        console.log('Fresh data: Calling scrollToLastDataPointInternal after 100ms delay');
        scrollToLastDataPointInternal();
      }, 100);
    } else {
      console.log('Fresh data: NOT scrolling (flag was false)');
    }

    // Always notify parent that loading is complete
    notifyLoadComplete();
  };

  // Function to mark R peaks and other markers on the chart
  const markPointsOnChart = function (chart, points) {
    console.log('markPointsOnChart called with', points?.length, 'markers');
    if (!chart || !chart.get) {
      console.warn('Invalid chart passed to markPointsOnChart');
      return;
    }
    
    let series = chart.get(PREVIEW_SERIES_NAME);
    if (!series) {
      console.warn('Series not found in markPointsOnChart:', PREVIEW_SERIES_NAME);
      return;
    }
    
    if (points && points.length > 0) {
      // Build a new data array with markers applied
      const newData = series.options.data.map(dataPoint => {
        // Find if this point has a marker
        const marker = points.find(m => m.x === dataPoint[0]);
        
        if (marker) {
          // Create point configuration with marker
          let smb = marker.isR ? 'rectangleUp' : 'rectangleDown';
          // Use semi-transparent colors so markers appear behind the ECG line
          let clr = 'rgba(0, 128, 0, 0.8)';  // All markers green
          let label = {};
          if (marker.title) {
            label.format = marker.title;
            label.enabled = true;
            label.x = 0;
            // R peaks: label on top (negative y), other markers: label at bottom of tick (positive y)
            label.y = marker.isR ? -15 : 15;
            label.verticalAlign = marker.isR ? 'bottom' : 'top';
          }

          return {
            x: dataPoint[0],
            y: dataPoint[1],
            marker: {
              symbol: smb,
              enabled: true,
              lineColor: clr,
              fillColor: clr,
              states: {
                hover: {
                  enabled: true
                }
              }
            },
            dataLabels: label
          };
        } else {
          // Return original point without marker
          return dataPoint;
        }
      });
      
      // Update the entire series with the new data including markers
      series.setData(newData, true);
    }
  };

  // Function to mark comment bubbles on the chart
  const markCommentPointsOnChart = function (chart, commentPoints) {
    console.log('markCommentPointsOnChart called with', commentPoints?.length, 'comment markers');
    if (!chart || !chart.get) {
      console.warn('Invalid chart passed to markCommentPointsOnChart');
      return;
    }
    
    if (!commentPoints || commentPoints.length === 0) {
      return;
    }
    
    // Remove existing comment series if it exists
    const existingCommentSeries = chart.get(COMMENT_MARKERS_PREVIEW_NAME);
    if (existingCommentSeries) {
      existingCommentSeries.remove(false);
    }
    
    // Get the current Y axis max to position markers at the top
    const yAxis = chart.yAxis[0];
    const yMax = yAxis.max || yAxis.dataMax || 0;
    
    // Create comment marker data points at the top of the chart
    const commentData = commentPoints.map(comment => {
      const maxLength = 50;
      let trimmedText = comment.messageText || '';
      if (trimmedText.length > maxLength) {
        trimmedText = trimmedText.substring(0, maxLength) + '...';
      }
      return {
        x: comment.x,
        y: yMax, // Position at the top of the chart
        messageType: comment.messageType,
        messageText: trimmedText
      };
    });
    
    // Add comment markers as a separate scatter series
    chart.addSeries({
      id: COMMENT_MARKERS_PREVIEW_NAME,
      type: 'scatter',
      name: 'Comments',
      data: commentData,
      showInLegend: false,
      enableMouseTracking: true,
      marker: {
        symbol: 'commentBubble',
        radius: 8,
        fillColor: '#FC1C19',
        lineColor: '#0000FF',
        lineWidth: 1,
        states: {
          hover: {
            enabled: true,
            fillColor: '#0000FF',
            radius: 10
          }
        }
      },
      tooltip: {        
        enabled: true,
        pointFormat: '{point.messageText}',
        headerFormat: ''
      },
      zIndex: 10, // Ensure markers appear on top
      stickyTracking: false
    }, false);
    
    chart.redraw();
  };

  // Function to clear markers from the chart
  const clearMarkersFromChart = function(chart) {
    if (!chart || !chart.get) return;
    
    let series = chart.get(PREVIEW_SERIES_NAME);
    if (!series) return;
    
    // Restore original data without markers
    if (cachedChartData.current) {
      series.setData(cachedChartData.current, true);
    }
  };

  // Function to toggle markers visibility
  const setMarkersVisibility = (isVisible) => {
    console.log('setMarkersVisibility called:', isVisible);
    markersEnabled.current = isVisible;
    
    const chart = getPreviewChart();
    if (!chart) {
      console.warn('Preview chart not available for markers toggle');
      return;
    }
    
    if (isVisible && cachedMarkers.current) {
      // Show markers
      markPointsOnChart(chart, cachedMarkers.current);
    } else {
      // Hide markers
      clearMarkersFromChart(chart);
    }
  };

  // Helper method to handle API error
  const handleApiError = (error) => {
    console.error('Error loading data chunk:', error);

    const currentChart = getPreviewChart();
    if (currentChart && currentChart.hideLoading) {
      try {
        currentChart.hideLoading();
      } catch (e) {
        console.warn('Error hiding loading on chart after error:', e);
      }
    }

    // Always notify parent that loading is complete
    notifyLoadComplete();
  };

  // Main loadDataChunk function - now much cleaner
  const loadDataChunk = (startTimeMs, endTimeMs, centerTimeMs, isLastRowParam = false) => {
    console.log('=== loadDataChunk START ===');
    console.log('loadDataChunk called with parameters:', {
      startTimeMs,
      endTimeMs,
      centerTimeMs,
      isLastRow: isLastRowParam,
      rowDurationMs: endTimeMs - startTimeMs,
    });

    const min = Math.round(startTimeMs);
    const max = Math.round(endTimeMs);
    const roundedCenter = Number.isFinite(centerTimeMs) ? Math.round(centerTimeMs) : null;

    // If there's already an identical in-flight request (including viewport intent), let it finish
    if (pendingApiController.current && pendingRequestRange.current &&
        pendingRequestRange.current.min === min &&
        pendingRequestRange.current.max === max &&
        pendingRequestRange.current.center === roundedCenter &&
        pendingRequestRange.current.isLastRow === !!isLastRowParam) {
      console.log('loadDataChunk: Identical request already in-flight, skipping duplicate');
      notifyLoadComplete();
      return;
    }

    // Cancel any pending API request before starting a new one
    if (pendingApiController.current) {
      console.log('loadDataChunk: Aborting previous API request');
      pendingApiController.current.abort();
      pendingApiController.current = null;
      pendingRequestRange.current = null;
    }

    // Save the isLastRow flag for later use (e.g., when adding online data)
    isLastRow.current = isLastRowParam;

    // Get chart early to extend empty space if needed
    const chart = getPreviewChart();

    // If this is the last row, extend empty space BEFORE any other checks
    // This ensures empty tail is added even if data loading prerequisites fail
    if (isLastRowParam && chart) {
      console.log('Last row detected - extending empty space to:', endTimeMs);
      extendSeriesDataToMax(chart, endTimeMs);
    } else if (extendedMaxTime.current !== null && chart) {
      // Not last row - clear extended max time and reset xAxis max to auto
      console.log('Not last row - clearing extended max time');
      extendedMaxTime.current = null;
      const xAxis = chart.xAxis[0];
      if (xAxis && xAxis.update) {
        xAxis.update({
          max: null // Reset to auto-calculated max based on data
        }, false);
        chart.redraw();
      }
    }

    // Validate prerequisites for data loading
    if (!asyncDataLoadUrlTemplate.current) {
      console.warn('No async data load URL template available');
      // Always notify parent that loading is complete
      notifyLoadComplete();
      return;
    }

    if (!chart) {
      console.warn('Preview chart not available for data loading');
      // Always notify parent that loading is complete
      notifyLoadComplete();
      return;
    }

    // If this is the last row, check if data is already present from online streaming
    // If data exists in this range, we don't need to fetch from server - just extend empty space
    if (isLastRowParam) {
      const series = chart.get(PREVIEW_SERIES_NAME);
      if (series && series.data && series.data.length > 0) {
        // Check if we have data points in the requested range
        const seriesData = series.data;
        const lastPoint = seriesData[seriesData.length - 1];
        const lastPointTime = lastPoint.x;

        // If the last point is within or beyond the requested start time,
        // we're in online streaming mode - data is already in the chart
        if (lastPointTime >= startTimeMs) {
          console.log('Last row + data already present: Skipping server fetch');
          console.log(`Last point time: ${lastPointTime}, requested start: ${startTimeMs}`);
          // Note: empty space already extended at top of function

          // Check if we should scroll to last point (for online streaming path)
          console.log('Online streaming path: Checking shouldScrollToLastPoint.current =', shouldScrollToLastPoint.current);
          if (shouldScrollToLastPoint.current) {
            console.log('Online streaming - scrolling to last point as requested');
            // Note: Flag will be reset inside scrollToLastDataPointInternal after scroll completes
            // Small delay to ensure viewport update is complete
            setTimeout(() => {
              console.log('Online streaming: Calling scrollToLastDataPointInternal after 100ms delay');
              scrollToLastDataPointInternal();
            }, 100);
          } else {
            console.log('Online streaming: NOT scrolling (flag was false), centering on clicked position');
            // Update viewport to center on the clicked position
            const xAxis = chart.xAxis[0];
            const currentExtremes = xAxis.getExtremes();
            const viewportWidth = currentExtremes.max - currentExtremes.min;

            // Center viewport on the clicked position
            const newStart = centerTimeMs - (viewportWidth / 2);
            const newEnd = centerTimeMs + (viewportWidth / 2);
            xAxis.setExtremes(newStart, newEnd, true, false);
          }

          notifyLoadComplete();
          console.log('=== loadDataChunk END (online streaming - no fetch) ===');
          return;
        }
      }
    }

    // Check if data is already cached
    if (isDataCached(min, max)) {
      console.log('Data already loaded for this range, updating viewport');
      updateViewportForCachedData(chart, centerTimeMs);

      // Note: if this is the last row, empty space already extended at top of function

      // Check if we should scroll to last point (for cached data path)
      console.log('Cached data path: Checking shouldScrollToLastPoint.current =', shouldScrollToLastPoint.current);
      if (shouldScrollToLastPoint.current) {
        console.log('Cached data - scrolling to last point as requested');
        // Note: Flag will be reset inside scrollToLastDataPointInternal after scroll completes
        // Small delay to ensure viewport update is complete
        setTimeout(() => {
          console.log('Cached data: Calling scrollToLastDataPointInternal after 100ms delay');
          scrollToLastDataPointInternal();
        }, 100);
      } else {
        console.log('Cached data: NOT scrolling (flag was false)');
      }

      // When using cached data, just notify parent that loading is complete
      notifyLoadComplete();

      console.log('=== loadDataChunk END (cached) ===');
      return;
    }

    // Load new data from API
    chart.showLoading(t('common.loadingFromServer'));

    const requestUrl = asyncDataLoadUrlTemplate.current + `&start=${min}&end=${max}`;
    console.log('Loading data from API:', requestUrl);

    // Create new AbortController for this request
    pendingApiController.current = new AbortController();
    pendingRequestRange.current = {
      min,
      max,
      center: roundedCenter,
      isLastRow: !!isLastRowParam
    };

    api
      .get(requestUrl, { signal: pendingApiController.current.signal })
      .then((response) => {
        handleApiResponse(response, centerTimeMs);
        loadedDataRange.current = { min, max };
        pendingApiController.current = null; // Clear controller after success
        pendingRequestRange.current = null;

        // Note: if this is the last row, empty space already extended at top of function
      })
      .catch((error) => {
        // Don't handle errors if the request was aborted
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          console.log('API request was aborted');
          return;
        }
        pendingApiController.current = null; // Clear controller after error
        pendingRequestRange.current = null;
        handleApiError(error);
      });
  };

  // Set the xAxis max to allow scrolling/panning to maxTimeMs
  // This shows empty grid space beyond the actual data without adding fake points
  const extendSeriesDataToMax = (chart, maxTimeMs) => {
    console.log('Setting xAxis max to:', maxTimeMs);

    // Store the extended max time for viewport calculations
    extendedMaxTime.current = maxTimeMs;

    // Update the xAxis max property to extend the scrollable range
    chart.xAxis[0].update({
      max: maxTimeMs
    }, false); // false = don't redraw yet

    chart.redraw();
    console.log('xAxis max updated to', maxTimeMs, '(stored in extendedMaxTime)');
  };

  // Internal function to scroll viewport to center on last data point
  const scrollToLastDataPointInternal = () => {
    console.log('scrollToLastDataPointInternal called');
    const chart = getPreviewChart();
    if (!chart) {
      console.warn('scrollToLastDataPointInternal: Chart not available');
      return;
    }

    // Use cached data instead of series.data (series might not be ready after setExtremes)
    if (!cachedChartData.current || cachedChartData.current.length === 0) {
      console.warn('scrollToLastDataPointInternal: No cached data available');
      return;
    }

    // Get the last data point from cached data
    const lastPoint = cachedChartData.current[cachedChartData.current.length - 1];
    const lastPointTime = Array.isArray(lastPoint) ? lastPoint[0] : lastPoint.x;

    console.log('scrollToLastDataPointInternal: Centering on last point at', lastPointTime);

    // Calculate viewport centered on last point
    const xAxis = chart.xAxis[0];
    const currentExtremes = xAxis.getExtremes();
    const viewportWidth = currentExtremes.max - currentExtremes.min;

    // Center viewport on the last data point
    const newStart = lastPointTime - (viewportWidth * 0.1);
    const newEnd = lastPointTime + (viewportWidth * 0.9);

    xAxis.setExtremes(newStart, newEnd, true, false);
    console.log('scrollToLastDataPointInternal: Viewport centered on last point');

    // Reset the flag after a delay to allow any concurrent data loads to complete
    // This prevents subsequent data loads from overriding the scroll position
    setTimeout(() => {
      console.log('scrollToLastDataPointInternal: Resetting shouldScrollToLastPoint flag (after delay)');
      shouldScrollToLastPoint.current = false;
    }, 500);
  };

  // Add new data points to the series (for online/realtime streaming)
  const addNewDataToChartLongSeries = (item) => {
    // Only add data if we're viewing the last row
    if (!isLastRow.current) {
      console.log('addNewDataToChartLongSeries: Not last row, ignoring data');
      return;
    }

    if (!item || !item.data || item.data.length === 0) {
      console.log('addNewDataToChartLongSeries: No data to add');
      return;
    }

    const chart = getPreviewChart();
    if (!chart || !chart.get) {
      console.warn('addNewDataToChartLongSeries: Chart not available');
      return;
    }

    const series = chart.get(PREVIEW_SERIES_NAME);
    if (!series) {
      console.warn('addNewDataToChartLongSeries: Series not found');
      return;
    }

    console.log('addNewDataToChartLongSeries: Adding', item.data.length, 'points');

    // STEP 1: Check if auto-scroll should be enabled
    const xAxis = chart.xAxis[0];
    const extremes = xAxis.getExtremes();
    let shouldAutoScroll = false;

    // Check forceAutoScroll flag first (one-time override)
    if (forceAutoScroll.current) {
      shouldAutoScroll = true;
      forceAutoScroll.current = false; // Reset immediately after reading
      console.log('Auto-scroll FORCED (one-time override)');
    } else if (series.data && series.data.length > 0) {
      // Normal check: is current last point visible in viewport?
      const currentLastPoint = series.data[series.data.length - 1];
      const currentLastTime = currentLastPoint.x;

      // Check if current last point is within the viewport
      if (currentLastTime >= extremes.min && currentLastTime <= extremes.max) {
        shouldAutoScroll = true;
        console.log('Auto-scroll enabled: last point is visible', currentLastTime);
      } else {
        console.log('Auto-scroll disabled: last point not visible', currentLastTime, 'viewport:', extremes.min, '-', extremes.max);
      }
    }

    // STEP 2: Add all points to the series (without redraw for performance)
    for (let i = 0; i < item.data.length; i++) {
      series.addPoint(item.data[i], false);
    }

    // Track the latest received time
    let newLastPointTime = null;
    if (item.data.length > 0) {
      const lastPoint = item.data[item.data.length - 1];
      newLastPointTime = Array.isArray(lastPoint) ? lastPoint[0] : lastPoint.x;
      latestReceivedTime.current = newLastPointTime;
      maxRecordLength.current = newLastPointTime;
    }

    // Redraw once after all points are added
    chart.redraw({ duration: 50 });

    // STEP 3: Auto-scroll logic
    if (shouldAutoScroll && newLastPointTime !== null) {
      const viewportWidth = extremes.max - extremes.min;
      const scrollThreshold = extremes.min + (viewportWidth * AUTO_SCROLL_TRIGGER_THRESHOLD);

      console.log('Auto-scroll check:', {
        newLastPointTime,
        scrollThreshold,
        viewportMax: extremes.max,
        triggerThreshold: `${AUTO_SCROLL_TRIGGER_THRESHOLD * 100}%`,
        shouldTrigger: newLastPointTime >= scrollThreshold
      });

      // If new last point crossed the threshold or is beyond viewport, scroll
      if (newLastPointTime >= scrollThreshold) {
        // Position new last point at the target position from viewport start
        const newMin = newLastPointTime - (viewportWidth * AUTO_SCROLL_TARGET_POSITION);
        const newMax = newMin + viewportWidth;

        console.log('Auto-scrolling viewport:', {
          newMin,
          newMax,
          targetPosition: `${AUTO_SCROLL_TARGET_POSITION * 100}%`
        });
        xAxis.setExtremes(newMin, newMax, true, false);
      } else {
        console.log(`No auto-scroll: new point still before ${AUTO_SCROLL_TRIGGER_THRESHOLD * 100}% threshold`);
      }
    }
  };

  const setChartExtremesX = function (chart, cellTimeMs, preferedPointStart) {
    console.log('[debug] setChartExtremesX called', cellTimeMs, preferedPointStart);
    currentChartSpeed.current = cellTimeMs;

    let xAxis = chart.xAxis[0],
      pointStart = preferedPointStart ?? xAxis.min,
      visibleTimeOnXAxis = calcMaxXAxisVisibleTimeMs(cellTimeMs),
      pointEnd = pointStart + visibleTimeOnXAxis;

    if (pointStart < xAxis.dataMin) pointStart = xAxis.dataMin;

    maxXAxisWidth.current = visibleTimeOnXAxis;

    if (maxRecordLength.current > 0 && pointEnd > maxRecordLength.current + visibleTimeOnXAxis) {
      pointEnd = (pointStart ?? 0) + visibleTimeOnXAxis;  // Fixed operator precedence
      //pointStart = pointEnd - visibleTimeOnXAxis;
      //console.log('fixed pointStart - ', pointStart, 'pointEnd - ', pointEnd);
      //console.log(chart);
    }

    //console.log('pointEnd - ', pointEnd);
    //console.log('min', xAxis.min, ' - ', xAxis.max);
    //console.log('dataMin', xAxis.dataMin, ' - ', xAxis.dataMax);

    //console.log('min:', xAxis.min, ' max:', xAxis.max, 'dataMin:', xAxis.dataMin, ' dataMax:', xAxis.dataMax);

    //console.log('pointStart-', pointStart ?? 0, ' pointEnd-', pointEnd);

    // Set extremes first without redraw to establish the view range
    xAxis.setExtremes(pointStart ?? 0, pointEnd, false);
    
    // Now update the axis tick properties with the new cell time
    // Force complete axis redraw with new minor tick interval
    setTimeout(() => {
        xAxis.update({ 
          minorTickInterval: cellTimeMs,
          tickPositioner: function () { return getChartTickPositions(this, cellTimeMs) },
          maxRange: visibleTimeOnXAxis + 1
        }, true); // Redraw after update to apply all changes
        chart.reflow();
     }, 500);

    return { visibleTimeOnXAxis: visibleTimeOnXAxis };
  };

  let calcMaxXAxisVisibleTimeMs = function (cellTimeMs) {
    let chart = getPreviewChart();
    let chartWidth = chart.xAxis[0].width; //chart.chartWidth
    let standardCellHeightInPx = chart.chartHeight * MAKE_SQUARE_CELLS_ON_ANY_MONITOR_COEFFICIENT / (TOTAL_CELLS_ON_Y * 2),
      visibleCellsX = chartWidth / standardCellHeightInPx,
      cellTime = cellTimeMs;

    console.log("When cell time is", cellTime, " then the length is ", cellTime * visibleCellsX);

    return cellTime * visibleCellsX;
  }; 

  // Expose methods via ref - all with console warnings for now
  useImperativeHandle(ref, () => ({
    setInitialData(data, seriesSettings) {
      console.warn('setInitialData called on EcgPreviewChart - not implemented yet');
    },
    setInitialDataForLongSeries(longData, asyncDataLoadUrl, seriesSettings, initialLoadRange) {
      console.warn('setInitialDataForLongSeries called on EcgPreviewChart - storing URL for async loading');
      asyncDataLoadUrlTemplate.current = asyncDataLoadUrl;

      // Preload first row data immediately if range provided (skip waiting for navigation)
      if (initialLoadRange) {
        setTimeout(() => {
          loadDataChunk(initialLoadRange.startMs, initialLoadRange.endMs, initialLoadRange.centerMs, false);
        }, 0);
      }
    },
    setDataViewMode(mode) {
      // Mode 0: Continuous (accumulation) - navigator only updates (not applicable with new nav)
      // Mode 1: Refresh mode - 10-second window with vertical line (currently not implemented)
      // Mode 2: Live view - updates chart with auto-scroll (default behavior)
      //
      // NOTE: In the new navigation implementation, modes 0 and 2 behave identically
      // because we don't have a separate "navigator" - the canvas navigation is always visible.
      // The auto-scroll logic in addNewDataToChartLongSeries handles live streaming for all modes.
      //
      // Refresh mode (mode 1) is not currently implemented but could be added later if needed.
      //viewModeRef.current = mode;
      console.log('EcgPreviewChart: setDataViewMode called with mode', mode, '(0=continuous, 1=refresh, 2=live)');
    },
    addNewDataToChart(data, seriesId) {
      console.warn('addNewDataToChart called on EcgPreviewChart - not implemented yet');
    },
    addNewDataToChartLongSeries(data) {
      addNewDataToChartLongSeries(data);
    },
    setupYAxis(settings) {
      console.warn('setupYAxis called on EcgPreviewChart - not implemented yet');
    },
    loadDataChunk(startTimeMs, endTimeMs, centerTimeMs, isLastRow) {
      // This is the main method we implement
      loadDataChunk(startTimeMs, endTimeMs, centerTimeMs, isLastRow);
    },
    moveChartToPosition(pointStart) {
      console.warn('moveChartToPosition called on EcgPreviewChart - not implemented yet');
    },
    setChartExtremesX(cellTimeMs, preferedPointStart) {
      console.warn('setChartExtremesX called on EcgPreviewChart', cellTimeMs, preferedPointStart);
      setChartExtremesX(getPreviewChart(), cellTimeMs, preferedPointStart);
    },
    setChartExtremesY(voltagePerCell) {
      console.warn('setChartExtremesY called on EcgPreviewChart');
      let
        chart = getPreviewChart(),
        yAxis = chart.yAxis[0];

      yAxis.update({ minorTickInterval: voltagePerCell, tickInterval: voltagePerCell * 10 });
      yAxis.setExtremes(- TOTAL_CELLS_ON_Y * voltagePerCell, TOTAL_CELLS_ON_Y * voltagePerCell);
    },
    setChartExtremesYMinMax(min, max) {
      console.warn('setChartExtremesYMinMax called on EcgPreviewChart - not implemented yet');
    },
    scrollToEnd() {
      console.log('scrollToEnd called on EcgPreviewChart - enabling force auto-scroll');
      // Set flag to force auto-scroll on next data arrival
      forceAutoScroll.current = true;
    },
    enableForceAutoScroll() {
      console.log('enableForceAutoScroll called on EcgPreviewChart');
      // Set flag to force auto-scroll on next data arrival
      forceAutoScroll.current = true;
    },
    setIsLastRow(value) {
      console.log('setIsLastRow called on EcgPreviewChart:', value);
      // Directly set the isLastRow flag
      isLastRow.current = value;
    },
    requestScrollToLastPoint() {
      console.log('=== EcgPreviewChart.requestScrollToLastPoint CALLED ===');
      console.log('requestScrollToLastPoint: Setting shouldScrollToLastPoint.current = true');
      // Set flag to scroll to last point after next data load completes
      shouldScrollToLastPoint.current = true;
      console.log('requestScrollToLastPoint: Flag set, shouldScrollToLastPoint.current =', shouldScrollToLastPoint.current);
    },
    scrollToLastDataPoint() {
      console.log('scrollToLastDataPoint called - scrolling immediately');
      scrollToLastDataPointInternal();
    },
    addAnnotation(newAnnotation) {
      console.warn('addAnnotation called on EcgPreviewChart - not implemented yet');
    },
    setPlotLinesX(plotLinesX) {
      console.warn('setPlotLinesX called on EcgPreviewChart - not implemented yet');
    },
    setMarkersVisibility(isVisible) {
      setMarkersVisibility(isVisible);
    },
    clearAllChartData() {
      console.warn('clearAllChartData called on EcgPreviewChart - clearing cache');
      loadedDataRange.current = { min: null, max: null };
      cachedChartData.current = null;
      cachedMarkers.current = null;
      isLastRow.current = false; // Reset last row flag
      latestReceivedTime.current = 0; // Reset latest received time
      markerEditingManager.reset(); // Reset marker editing state
      forceAutoScroll.current = false; // Reset force auto-scroll flag
      extendedMaxTime.current = null; // Reset extended max time
      shouldScrollToLastPoint.current = false; // Reset scroll to last point flag
      const chart = getPreviewChart();
      if (chart) {
        const series = chart.get(PREVIEW_SERIES_NAME);
        if (series) {
          series.setData([], true);
        }
      }
    },
    updateComments(newComments) {
      console.warn('updateComments called on EcgPreviewChart - not implemented yet');
    },
    // Edit mode methods - use these instead of props to avoid re-renders
    setEditMode(enabled) {
      console.log('setEditMode called:', enabled);
      isEditModeRef.current = enabled;
      if (!enabled) {
        // Clear pending changes when exiting edit mode
        markerEditingManager.clearPendingChanges();
      }
    },
    setSelectedMarkerType(type) {
      console.log('setSelectedMarkerType called:', type);
      markerEditingManager.setSelectedMarkerType(type);
    },
    updatePendingMarkers(additions, deletions) {
      // Sync manager state with external state (from parent component)
      markerEditingManager.pendingAdditions = additions || [];
      markerEditingManager.pendingDeletions = deletions || [];
      updateMarkerDisplay();
    },
    addPendingMarker(marker) {
      markerEditingManager.addMarker(marker.x, marker.type);
      updateMarkerDisplay();
    },
    removePendingMarker(marker) {
      // Check if it was in pending additions
      if (markerEditingManager.findPendingAdditionNear(marker.x)) {
        markerEditingManager.removePendingAddition(marker.x);
      } else {
        markerEditingManager.markForDeletion(marker.x, marker.type || 'R');
      }
      updateMarkerDisplay();
    },
    // Get pending changes for saving
    getPendingChanges() {
      return markerEditingManager.getPendingChanges();
    },
    // Check if there are unsaved changes
    hasPendingChanges() {
      return markerEditingManager.hasPendingChanges();
    }
  })); 

  const getPreviewOptions = () => {
    // Get translations for menu items
    const fullScreenText = t('chart.viewFullScreenButton');
    const exportPictureText = t('chart.exportPictureButton');
    const generatingImageText = t('chart.generatingImage');

    let commonOptions = ChartConsts.regularChartOptions;
    let newOptions = {
      //boost: {
      //  enabled: true,              // Enable boost module for better performance
      //  useGPUTranslations: true,   // Use GPU for calculations if available
      //  seriesThreshold: 1          // Use boost for all series
      //},
      chart: {
        events: {
          click: function(e) {
            // Get clicked X from event (works even after chart redraw when hoverPoint is lost)
            let clickedX = null;

            // Try to get X from hoverPoint first (most accurate)
            if (this.hoverPoint) {
              clickedX = this.hoverPoint.x;
            } else if (e.xAxis && e.xAxis[0]) {
              // Fallback: use event coordinates and find nearest data point
              const eventX = e.xAxis[0].value;
              // Find nearest point in cached data
              if (cachedChartData.current && cachedChartData.current.length > 0) {
                let nearestPoint = cachedChartData.current[0];
                let minDistance = Math.abs((Array.isArray(nearestPoint) ? nearestPoint[0] : nearestPoint.x) - eventX);

                for (const point of cachedChartData.current) {
                  const pointX = Array.isArray(point) ? point[0] : point.x;
                  const distance = Math.abs(pointX - eventX);
                  if (distance < minDistance) {
                    minDistance = distance;
                    nearestPoint = point;
                  }
                }
                clickedX = Array.isArray(nearestPoint) ? nearestPoint[0] : nearestPoint.x;
              }
            }

            if (clickedX !== null) {
              console.log('Clicked point X value:', clickedX);

              // Handle marker editing if in edit mode
              if (isEditModeRef.current) {
                const { action, marker } = markerEditingManager.handleClick(clickedX);
                console.log('Marker action:', action, marker);

                // Notify parent based on action
                if (action === 'added' && onMarkerAddedRef.current) {
                  onMarkerAddedRef.current(marker);
                } else if ((action === 'removed_pending' || action === 'marked_for_deletion') && onMarkerDeletedRef.current) {
                  onMarkerDeletedRef.current(marker);
                }

                // Update chart display
                updateMarkerDisplay();
              } else if (onRPeakClickRef.current && cachedMarkers.current) {
                // Not in edit mode - check for R-peak cycle selection
                const rPeaks = cachedMarkers.current.filter(m => m.isR);
                if (rPeaks.length > 0) {
                  // Find the nearest R-peak within reasonable proximity (300ms threshold)
                  const PROXIMITY_THRESHOLD_MS = 300;
                  let nearestRPeak = null;
                  let minDistance = PROXIMITY_THRESHOLD_MS;

                  rPeaks.forEach((peak) => {
                    const distance = Math.abs(peak.x - clickedX);
                    if (distance < minDistance) {
                      minDistance = distance;
                      nearestRPeak = peak;
                    }
                  });

                  if (nearestRPeak) {
                    // Cycle number from title if available, otherwise use "-"
                    const titleNum = nearestRPeak.title ? parseInt(nearestRPeak.title, 10) : NaN;
                    const cycleNumber = isNaN(titleNum) ? null : titleNum;
                    const displayTitle = nearestRPeak.title || '-';

                    console.log('R-peak clicked:', displayTitle, nearestRPeak);
                    onRPeakClickRef.current(cycleNumber, {
                      number: cycleNumber,
                      timeMs: nearestRPeak.x,
                      y: nearestRPeak.y || 0,
                      title: displayTitle
                    });
                  }
                }
              }
            }
          },
          load: function (x) {
            if (props.onChartLoadEvent)
              props.onChartLoadEvent(this);

            // Report chart width to parent
            if (props.onChartRendered && this.chartWidth) {
              props.onChartRendered(this.chartWidth);
            }

            setTimeout(() => {
              try {
                this.reflow();
                // Report width again after reflow
                if (props.onChartRendered && this.chartWidth) {
                  props.onChartRendered(this.chartWidth);
                }
              }
              catch { }
            }, 100);

            //if (props.defaultCellTimeXms) {
            //  let xTimeMs = props.defaultCellTimeXms;

            //  setChartExtremesX(this, xTimeMs, 0);
            //  //this is required as otherwise the whole series is selected when panning on x axis.
            //  //Because of an extra series. Maybe add the extra series later ??
            //  //setTimeout(() => updateChart(40, 0), 2000);
            //  setTimeout(() => {
            //    try {
            //      setChartExtremesX(this, xTimeMs, 0)
            //    } catch { }
            //  }, 200);
            //}
          },
          redraw: function() {
            // Report chart width on every redraw
            if (props.onChartRendered && this.chartWidth) {
              props.onChartRendered(this.chartWidth);
            }
          },
          resize: function() {
            // Report chart width on resize
            if (props.onChartRendered && this.chartWidth) {
              props.onChartRendered(this.chartWidth);
            }
          }
        },
      },
      exporting: {
        menuItemDefinitions: {
          custom: {
            onclick: function () {
              this.fullscreen.toggle();
            },
            text: fullScreenText
          },
          exportPicture: {
            onclick: async function () {
              const visibleStartTime = Math.round(this.xAxis[0].min);

              if (!props.baseRecordUrl) {
                console.error('Base record URL not available');
                return;
              }

              console.log('Export Picture clicked');
              console.log('Visible start time:', visibleStartTime, 'ms');

              try {
                this.showLoading(generatingImageText);

                // Make API call to get the image
                // baseRecordUrl is dynamic: /user/records/{recordUid} or /consultation/expert/.../user/record/{recordUid}
                // baseURL is already "/api/v1/front/" in api service
                const response = await api.get(
                  `${props.baseRecordUrl}/export-png?startTime=${visibleStartTime}`,
                  { responseType: 'blob' }
                );

                // Hide loading indicator
                this.hideLoading();

                // Convert blob to base64 data URL for better compatibility with Microsoft Word
                const blob = response.data;
                const reader = new FileReader();

                reader.onloadend = function() {
                  const base64data = reader.result;

                  // Generate a unique filename based on timestamp
                  const timestamp = new Date().getTime();
                  const filename = `ecg-export-${timestamp}.png`;

                  // Create an HTML wrapper page with the image embedded as base64
                  // This allows proper copy-paste functionality, including in Microsoft Word
                  const html = `
                    <!DOCTYPE html>
                    <html>
                      <head>
                        <title>ECG Export</title>
                        <style>
                          body {
                            margin: 0;
                            display: flex;
                            flex-direction: column;
                            justify-content: center;
                            align-items: center;
                            min-height: 100vh;
                            background: #f0f0f0;
                            padding: 20px;
                          }
                          img {
                            max-width: 100%;
                            height: auto;
                            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                          }
                          .download-link {
                            margin-top: 15px;
                            padding: 10px 20px;
                            background: #007bff;
                            color: white;
                            text-decoration: none;
                            border-radius: 4px;
                            font-family: Arial, sans-serif;
                          }
                          .download-link:hover {
                            background: #0056b3;
                          }
                        </style>
                      </head>
                      <body>
                        <img src="${base64data}" alt="ECG Export" />
                        <a href="${base64data}" download="${filename}" class="download-link">Download</a>
                      </body>
                    </html>
                  `;

                  // Open in new tab with HTML wrapper
                  const newTab = window.open();
                  if (!newTab) {
                    console.error('Failed to open new tab - popup might be blocked');
                    alert('Please allow popups to view the exported image');
                    return;
                  }

                  newTab.document.write(html);
                  newTab.document.close();
                };

                reader.readAsDataURL(blob);

              } catch (error) {
                this.hideLoading();
                console.error('Error exporting picture:', error);
                alert('Failed to export picture. Please try again.');
              }
            },
            text: exportPictureText
          }
        },
        buttons: {
          contextButton: {
            menuItems: ['custom', 'exportPicture'],
            x: -25
          }
        }
      },
      scrollbar: {
        liveRedraw: true  // Enable smooth scrolling while dragging
      },
      plotOptions: {
        series: {
          point: {
            events: {
              click: function() {
                // Handle clicks on data points/markers (complements chart.events.click)
                const clickedX = this.x;

                // Handle marker editing if in edit mode
                if (isEditModeRef.current) {
                  const { action, marker } = markerEditingManager.handleClick(clickedX);
                  console.log('Marker action:', action, marker);

                  // Notify parent based on action
                  if (action === 'added' && onMarkerAddedRef.current) {
                    onMarkerAddedRef.current(marker);
                  } else if ((action === 'removed_pending' || action === 'marked_for_deletion') && onMarkerDeletedRef.current) {
                    onMarkerDeletedRef.current(marker);
                  }

                  // Update chart display
                  updateMarkerDisplay();
                } else if (onRPeakClickRef.current && cachedMarkers.current) {
                  // Not in edit mode - check for R-peak cycle selection
                  const rPeaks = cachedMarkers.current.filter(m => m.isR);
                  if (rPeaks.length > 0) {
                    // Find the nearest R-peak within reasonable proximity (300ms threshold)
                    const PROXIMITY_THRESHOLD_MS = 300;
                    let nearestRPeak = null;
                    let minDistance = PROXIMITY_THRESHOLD_MS;

                    rPeaks.forEach((peak) => {
                      const distance = Math.abs(peak.x - clickedX);
                      if (distance < minDistance) {
                        minDistance = distance;
                        nearestRPeak = peak;
                      }
                    });

                    if (nearestRPeak) {
                      // Cycle number from title if available, otherwise use "-"
                      const titleNum = nearestRPeak.title ? parseInt(nearestRPeak.title, 10) : NaN;
                      const cycleNumber = isNaN(titleNum) ? null : titleNum;
                      const displayTitle = nearestRPeak.title || '-';

                      console.log('R-peak clicked (point):', displayTitle, nearestRPeak);
                      onRPeakClickRef.current(cycleNumber, {
                        number: cycleNumber,
                        timeMs: nearestRPeak.x,
                        y: nearestRPeak.y || 0,
                        title: displayTitle
                      });
                    }
                  }
                }
              }
            }
          }
        }
      },
      xAxis: {
        maxRange: maxXAxisWidth.current + 1,
        tickPositioner: function () { return getChartTickPositions(this, currentChartSpeed.current ?? props.defaultCellTimeXms) },
        labels: {
          step: 2  // Show every second label to prevent overlap
        },
        events: {
          afterSetExtremes: function (e) {
            // Throttle viewport updates to prevent too many calls
            if (props.onViewportChanged && e.min !== undefined && e.max !== undefined) {
              // Clear existing timeout
              if (viewportUpdateTimeout.current) {
                clearTimeout(viewportUpdateTimeout.current);
              }

              // Set new timeout for throttled update
              viewportUpdateTimeout.current = setTimeout(() => {
                props.onViewportChanged(e.min, e.max);
                if (props.extremesChangedCallback) {
                  // Pass min and max directly, not the event object
                  props.extremesChangedCallback(e.min, e.max);
                }

                viewportUpdateTimeout.current = null;
              }, 300); //  throttle
            }

            // Legacy callback without throttling
          }
        },
      }
    };
    let common = mergeDeep(commonOptions, newOptions);

    if (props.specificChartOptions) {
      return mergeDeep(common, props.specificChartOptions);
    }

    return common;
  }

  return (
    <HighchartsReact
      ref={chartPreviewComponentRef}
      highcharts={Highcharts}
      constructorType={'chart'}
      options={getPreviewOptions()}
    />
  );
});

EcgPreviewChartForwardRef.displayName = 'EcgPreviewChartForwardRef';

export const EcgPreviewChart = React.memo(EcgPreviewChartForwardRef);
